import type { BlogAuthor, ContentCredit } from "./types";
import { ORG_ID } from "@/lib/seo/graph";
import type { BlogAuthorRecord } from "@/lib/blogPublishing";

/**
 * Default byline: the editorial team is an organisation label, not a person,
 * so schema references the Organization node. Replace per article only with
 * real, verified people (see src/content/trust/experts.ts).
 */
export const DEFAULT_BLOG_AUTHOR: BlogAuthor = {
  kind: "organization",
  name: "LeadXkids Editorial Team",
  role: "Editorial team",
  bio: "The LeadXkids editorial team creates practical educational guides for parents of children aged 8–14. These articles are general education, not diagnosis or treatment.",
  photoUrl: "https://leadxkids.com/lx-logo-new.webp",
  profileUrl: "https://leadxkids.com/editorial-policy",
};

export function registeredBlogAuthor(record: BlogAuthorRecord): BlogAuthor {
  return { kind: record.kind, name: record.name, role: record.role, bio: record.bio,
    photoUrl: record.photo_url, profileUrl: record.profile_url ?? `https://leadxkids.com/blog/authors#${record.id}` };
}

export function resolveCredit(meta: { author?: BlogAuthor; credit?: ContentCredit }): ContentCredit {
  return meta.credit ?? { author: meta.author ?? DEFAULT_BLOG_AUTHOR };
}

/** JSON-LD for an author/reviewer. Organisation labels point at the org node. */
export function contributorSchema(c: BlogAuthor): Record<string, unknown> {
  if (c.kind !== "person") return { "@type": "Organization", "@id": ORG_ID, name: "LeadXkids" };
  return {
    "@type": "Person",
    ...(c.id ? { "@id": c.id } : {}),
    name: c.name,
    ...(c.bio ? { description: c.bio } : {}),
    ...(c.photoUrl ? { image: c.photoUrl } : {}),
    ...(c.role ? { jobTitle: c.role } : {}),
    ...(c.profileUrl ? { url: c.profileUrl } : {}),
    ...(c.credentials?.length
      ? { hasCredential: c.credentials.map((n) => ({ "@type": "EducationalOccupationalCredential", name: n })) }
      : {}),
  };
}
